import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { DragDropCanvas } from '@/components/ui/DragDropCanvas'
import { 
  Workflow, 
  ArrowRight, 
  Loader2, 
  Play, 
  CheckCircle, 
  Clock,
  Target,
  Lightbulb,
  Zap,
  Home
} from 'lucide-react'
import { useJourneyStore } from '@/stores/journeyStore'

interface SimulationStep {
  id: string
  title: string
  description: string
  status: 'pending' | 'running' | 'completed'
  duration?: number
}

export function Stage3Automation() {
  const navigate = useNavigate()
  const { completeStage, updateStageProgress } = useJourneyStore()
  const [isCompleting, setIsCompleting] = useState(false)
  const [completionMessage, setCompletionMessage] = useState('')
  const [workflow, setWorkflow] = useState<any[]>([])
  const [connections, setConnections] = useState<any[]>([])
  const [isSimulating, setIsSimulating] = useState(false)
  const [simulationSteps, setSimulationSteps] = useState<SimulationStep[]>([])
  const [workflowsCreated, setWorkflowsCreated] = useState(0)
  const [simulationsRun, setSimulationsRun] = useState(0)

  const automationTemplates = [
    {
      id: 'lead-followup',
      title: 'Automated Lead Follow-up',
      description: 'When a contact form is submitted, save lead to CRM, notify sales team, and send welcome email',
      blocks: 3,
      difficulty: 'Beginner',
      timeToImplement: '5 minutes',
      roi: 'High'
    },
    {
      id: 'social-posting',
      title: 'Social Media Automation',
      description: 'Auto-post new blog articles to LinkedIn, Twitter, and Facebook with custom messaging',
      blocks: 4,
      difficulty: 'Intermediate',
      timeToImplement: '10 minutes',
      roi: 'Medium'
    },
    {
      id: 'invoice-generation',
      title: 'Invoice & Project Setup',
      description: 'When a deal is won, generate invoice, create project folder, and notify team',
      blocks: 5,
      difficulty: 'Advanced',
      timeToImplement: '15 minutes',
      roi: 'Very High'
    }
  ]

  const handleWorkflowChange = (blocks: any[], connections: any[]) => {
    setWorkflow(blocks)
    setConnections(connections)
    
    if (blocks.length > 0 && workflowsCreated === 0) {
      setWorkflowsCreated(1)
    }

    updateStageProgress(3, {
      interactionData: { 
        blocksPlaced: blocks.length,
        connectionsCreated: connections.length,
        workflowsCreated,
        simulationsRun
      }
    })
  }

  const simulateWorkflow = async () => {
    if (workflow.length === 0) {
      alert('Please create a workflow first by dragging blocks to the canvas!')
      return
    }

    setIsSimulating(true)
    
    // Generate simulation steps based on workflow
    const steps: SimulationStep[] = workflow.map((block, index) => ({
      id: block.id,
      title: block.title,
      description: `Processing: ${block.description}`,
      status: 'pending' as const,
      duration: Math.random() * 2000 + 1000 // 1-3 seconds
    }))

    setSimulationSteps(steps)

    // Simulate each step
    for (let i = 0; i < steps.length; i++) {
      setSimulationSteps(prev => prev.map((step, index) => 
        index === i ? { ...step, status: 'running' } : step
      ))

      await new Promise(resolve => setTimeout(resolve, steps[i].duration))

      setSimulationSteps(prev => prev.map((step, index) => 
        index === i ? { ...step, status: 'completed' } : step
      ))
    }

    setIsSimulating(false)
    setSimulationsRun(prev => prev + 1)
    
    updateStageProgress(3, {
      interactionData: { 
        blocksPlaced: workflow.length,
        connectionsCreated: connections.length,
        workflowsCreated,
        simulationsRun: simulationsRun + 1
      }
    })
  }

  const loadTemplate = (templateId: string) => {
    // This would load a pre-built workflow template
    // For demo purposes, we'll just show that templates are available
    alert(`Loading ${templateId} template... (This would pre-populate the canvas with a working workflow)`)
  }

  const handleComplete = async () => {
    if (isCompleting) return
    
    setIsCompleting(true)
    setCompletionMessage('Completing stage...')
    
    try {
      const stageData = {
        blocksPlaced: workflow.length,
        connectionsCreated: connections.length,
        workflowsCreated,
        simulationsRun,
        completed: true
      }
      
      completeStage(3, stageData)
      setCompletionMessage('Stage completed! Moving to next stage...')
      
      // Let the store's auto-advancement handle navigation
      // No immediate navigation to dashboard
    } catch (error) {
      console.error('Error completing stage:', error)
      setCompletionMessage('Error completing stage. Please try again.')
      setIsCompleting(false)
    }
  }

  const handleSkip = async () => {
    if (isCompleting) return
    
    setIsCompleting(true)
    setCompletionMessage('Skipping to next stage...')
    
    try {
      completeStage(3, { skipped: true })
      
      // Let the store's auto-advancement handle navigation
      // No immediate navigation to dashboard
    } catch (error) {
      console.error('Error skipping stage:', error)
      setCompletionMessage('Error skipping stage. Please try again.')
      setIsCompleting(false)
    }
  }

  const canComplete = workflowsCreated > 0 && simulationsRun > 0

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Stage Header */}
      <div className="text-center space-y-4">
        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
          <Workflow className="w-8 h-8 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Stage 3: Process Automation Playground</h1>
          <p className="text-lg text-muted-foreground">Build your first automated workflows visually</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Main Canvas */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" />
                Workflow Builder
              </CardTitle>
              <CardDescription>
                Drag blocks from the palette to create automated business processes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DragDropCanvas 
                onWorkflowChange={handleWorkflowChange}
                onSimulate={simulateWorkflow}
              />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Your Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Blocks placed:</span>
                <span className="font-medium">{workflow.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Connections made:</span>
                <span className="font-medium">{connections.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Workflows created:</span>
                <span className="font-medium">{workflowsCreated}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Simulations run:</span>
                <span className="font-medium">{simulationsRun}</span>
              </div>
              
              <div className="space-y-2 mt-4">
                {completionMessage && (
                  <div className="text-center p-2 bg-primary/10 text-primary rounded-md text-sm">
                    {completionMessage}
                  </div>
                )}
                
                {canComplete && (
                  <Button 
                    onClick={handleComplete} 
                    disabled={isCompleting}
                    className="w-full gap-2"
                  >
                    {isCompleting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>Complete Stage <ArrowRight className="w-4 h-4" /></>
                    )}
                  </Button>
                )}
                
                <Button 
                  onClick={handleSkip} 
                  variant="outline" 
                  disabled={isCompleting}
                  className="w-full gap-2"
                >
                  {isCompleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>Skip to Next Stage <ArrowRight className="w-4 h-4" /></>
                  )}
                </Button>
                
                <Button 
                  onClick={() => navigate('/')} 
                  variant="ghost" 
                  className="w-full gap-2"
                >
                  <Home className="w-4 h-4" />
                  Back to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Automation Templates */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-600" />
                Automation Templates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {automationTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => loadTemplate(template.id)}
                  >
                    <h4 className="font-medium text-sm mb-1">{template.title}</h4>
                    <p className="text-xs text-muted-foreground mb-2">{template.description}</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        {template.blocks} blocks
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {template.timeToImplement}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Simulation Results */}
          {simulationSteps.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Play className="w-5 h-5 text-green-600" />
                  Simulation Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {simulationSteps.map((step, index) => (
                    <div key={step.id} className="flex items-center gap-2 text-sm">
                      <div className="flex-shrink-0">
                        {step.status === 'pending' && (
                          <div className="w-4 h-4 rounded-full border-2 border-muted" />
                        )}
                        {step.status === 'running' && (
                          <div className="w-4 h-4 rounded-full border-2 border-primary animate-spin" />
                        )}
                        {step.status === 'completed' && (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{step.title}</div>
                        <div className="text-xs text-muted-foreground">{step.description}</div>
                      </div>
                    </div>
                  ))}
                  
                  {!isSimulating && simulationSteps.every(s => s.status === 'completed') && (
                    <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/20 rounded text-sm text-green-700 dark:text-green-400">
                      ✓ Workflow executed successfully! This automation would save approximately 15 minutes per lead.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}